import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Users, Plus, Copy, Trash } from 'lucide-react';
import { OrgRole } from '../../types/enums';
import { toast } from 'react-hot-toast';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';

interface Member {
  id: string;
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  role: OrgRole;
}

interface Invite {
  id: string;
  code: string;
  role: OrgRole;
  email: string | null;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  createdAt: string;
}

export function MembersAndInvites() {
  const { orgId } = useParams<{ orgId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [createInviteModalOpen, setCreateInviteModalOpen] = useState(false);
  const [newInvite, setNewInvite] = useState({
    role: OrgRole.MEMBER as OrgRole,
    email: '',
    maxUses: null as number | null,
    expiresAt: null as string | null,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Load members
      const membersResponse = await fetchWithAuth(`/api/organizations/${orgId}/members`, {
        token
      });
      setMembers(membersResponse.data);

      // Load invites
      const invitesResponse = await fetchWithAuth(`/api/organizations/${orgId}/invites`, {
        token
      });
      setInvites(invitesResponse.data);
    } catch (error) {
      console.error('Error loading members and invites:', error);
      toast.error('Failed to load members and invites');
    } finally {
      setLoading(false);
    }
  }, [orgId, getAccessTokenSilently]);

  const createInvite = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetchWithAuth(`/api/organizations/${orgId}/invites`, {
        method: 'POST',
        token,
        body: JSON.stringify(newInvite)
      });
      setInvites([...invites, response.data]);
      setCreateInviteModalOpen(false);
      setNewInvite({
        role: OrgRole.MEMBER,
        email: '',
        maxUses: null,
        expiresAt: null,
      });
      toast.success('Invite created successfully');
    } catch (error) {
      console.error('Error creating invite:', error);
      toast.error('Failed to create invite');
    }
  };

  const revokeInvite = async (inviteId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetchWithAuth(`/api/organizations/${orgId}/invites/${inviteId}`, {
        method: 'DELETE',
        token
      });
      setInvites(invites.filter(invite => invite.id !== inviteId));
      toast.success('Invite revoked successfully');
    } catch (error) {
      console.error('Error revoking invite:', error);
      toast.error('Failed to revoke invite');
    }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied to clipboard');
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6">
      {/* Members Section */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Users className="w-6 h-6 mr-2 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-100">Members</h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {members?.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-center text-gray-400">
                      No members found
                    </td>
                  </tr>
                ) : (
                  members?.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {member.user.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {member.user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {member.role}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invites Section */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Users className="w-6 h-6 mr-2 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-100">Invites</h2>
          </div>
          <button
            type="button"
            onClick={() => setCreateInviteModalOpen(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Invite
          </button>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Uses</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {invites?.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-400">
                      No invites found
                    </td>
                  </tr>
                ) : (
                  invites?.map((invite) => (
                    <tr key={invite.id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <div className="flex items-center">
                          <span className="font-mono">{invite.code}</span>
                          <button
                            type="button"
                            onClick={() => copyInviteCode(invite.code)}
                            className="ml-2 text-gray-400 hover:text-gray-300"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {invite.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {invite.email || 'Any'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {invite.useCount}/{invite.maxUses || '∞'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        {invite.expiresAt ? new Date(invite.expiresAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                        <button
                          type="button"
                          onClick={() => revokeInvite(invite.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Invite Modal */}
      {createInviteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-gray-100 mb-4">Create Invite</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-300 mb-1">
                  Role
                </label>
                <select
                  id="inviteRole"
                  value={newInvite.role}
                  onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value as OrgRole })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={OrgRole.MEMBER}>Member</option>
                  <option value={OrgRole.ADMIN}>Admin</option>
                </select>
              </div>

              <div>
                <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-300 mb-1">
                  Email (Optional)
                </label>
                <input
                  id="inviteEmail"
                  type="email"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                  placeholder="Leave empty for any email"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="inviteMaxUses" className="block text-sm font-medium text-gray-300 mb-1">
                  Max Uses (Optional)
                </label>
                <input
                  id="inviteMaxUses"
                  type="number"
                  value={newInvite.maxUses || ''}
                  onChange={(e) => setNewInvite({ ...newInvite, maxUses: e.target.value ? Number.parseInt(e.target.value) : null })}
                  placeholder="Leave empty for unlimited"
                  min="1"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="inviteExpiresAt" className="block text-sm font-medium text-gray-300 mb-1">
                  Expires At (Optional)
                </label>
                <input
                  id="inviteExpiresAt"
                  type="datetime-local"
                  value={newInvite.expiresAt || ''}
                  onChange={(e) => setNewInvite({ ...newInvite, expiresAt: e.target.value || null })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setCreateInviteModalOpen(false)}
                className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createInvite}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 